/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { pluck } from 'underscore'
import Modal from '~/components/Modal'
import { Button } from '~/components/ui'
import { useServiceContext } from '~/root'

interface ApplyPackageReviewStepProps {
  packageData: {
    id?: number
    name?: string
    filePlanName?: string
    entries?: Array<{
      name?: string
      alias?: string
      docTypeId?: number | string
    }>
    [key: string]: any
  }
  config: {
    levels?: {
      level?: string
      product?: { fsiName?: string }
      trial?: { fsiName?: string }
      environment?: { fsiName?: string }
      countries?: Array<{ fsiName?: string }>
      sites?: Array<{ fsiName?: string }>
    }
    tags?: {
      actions?: {
        new?: boolean
        associate?: boolean
      }
      tagName?: string
      tags?: Array<{ name?: string; value?: any }>
    }
    [key: string]: any
  }
  options: {
    docTypesMap?: Record<number | string, { name?: string }>
    [key: string]: any
  }
  nextStep: (packageData: any, options: any, config: any, step: string) => void
  doClose: () => void
  save: () => void
}

const ApplyPackageReviewStep: React.FC<ApplyPackageReviewStepProps> = ({
  packageData,
  config,
  options,
  nextStep,
  doClose,
  save
}) => {
  const context = useServiceContext()
  const { translate } = context.service.filter
  const [countriesConcat, setCountriesConcat] = useState('')
  const [sitesConcat, setSitesConcat] = useState('')
  const [tagsArray, setTagsArray] = useState<string[]>([])

  useEffect(() => {
    setCountriesConcat(pluck(config?.levels?.countries || [], 'fsiName').join(', '))
    setSitesConcat(pluck(config?.levels?.sites || [], 'fsiName').join(', '))

    const tags: string[] = []
    if (config?.tags?.actions?.new && config?.tags?.tagName) {
      tags.push(config.tags.tagName)
    }
    if (config?.tags?.actions?.associate && config?.tags?.tags) {
      const tagNames = pluck(config.tags.tags, 'name').filter((name): name is string => !!name)
      tags.push(...tagNames)
    }
    setTagsArray(tags)
  }, [])

  return (
    <>
      <Modal.Body>
        <div className="step-review">
          <div className="row no-gutters">
            <div className="col-xs-10">
              <div className="form-group w100p">
                <div>{translate('SETTINGS_PACKAGE_EDIT_REVIEW_INTRO_TXT')}</div>
              </div>
              <div className="review-container">
                <div className="review-label">{translate('SETTINGS_PACKAGE_NAME')}</div>
                <div className="review-text">{packageData?.name}</div>
              </div>
              <div className="review-container">
                <div className="review-label">{translate('EMTF_LEVEL')}</div>
                <div className="review-text">{config?.levels?.level}</div>
              </div>
              <div className="review-container">
                <div className="review-label">{translate('FILE_PLAN')}</div>
                <div className="review-text">{packageData?.filePlanName}</div>
              </div>
              {config?.levels?.product && (
                <div className="review-container">
                  <div className="review-label">{translate('EMTF_MD_PRODUCT')}</div>
                  <div className="review-text">{config?.levels?.product?.fsiName}</div>
                </div>
              )}
              {config?.levels?.trial && (
                <div className="review-container">
                  <div className="review-label">{translate('EMTF_MD_TRIAL')}</div>
                  <div className="review-text">{config?.levels?.trial?.fsiName}</div>
                </div>
              )}
              {config?.levels?.environment && (
                <div className="review-container">
                  <div className="review-label">{translate('EMTF_MD_ENVIRONMENT')}</div>
                  <div className="review-text">{config?.levels?.environment?.fsiName}</div>
                </div>
              )}
              {!!config?.levels?.countries?.length && (
                <div className="review-container">
                  <div className="review-label">{translate('EMTF_MD_COUNTRY(IES)')}</div>
                  <div className="review-text">{countriesConcat}</div>
                </div>
              )}
              {!!config?.levels?.sites?.length && (
                <div className="review-container">
                  <div className="review-label">{translate('EMTF_MD_SITE(S)')}</div>
                  <div className="review-text">{sitesConcat}</div>
                </div>
              )}
              <div className="artifacts-table review-container">
                <div className="review-label">{translate('SETTINGS_PACKAGE_ARTIFACTS')}</div>
                <div className="review-text max-height">
                  <table className="w100p">
                    <thead>
                      <tr>
                        <th>{translate('ETMF_ARTIFACT')}</th>
                        <th>{translate('SETTINGS_PACKAGE_CONTENTS_PLACEHOLDER_NAME')}</th>
                        <th>{translate('DOCUMENT_TYPE')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {packageData?.entries?.map((entry, index) => (
                        <tr key={index}>
                          <td>{entry?.name}</td>
                          <td>{entry?.alias}</td>
                          <td>{options?.docTypesMap?.[entry?.docTypeId ?? '']?.name}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              {!!tagsArray?.length && (
                <div className="review-container">
                  <div className="review-label">{translate('SETTINGS_PACKAGE_TAGS')}</div>
                  <div className="review-text max-height">
                    {tagsArray?.map((tagName, index) => (
                      <div key={index}>{tagName}</div>
                    ))}
                  </div>
                </div>
              )}
              <div className="form-group w100p mb0 fs12">
                <b>{translate('SETTINGS_PACKAGE_APPLY_REVIEW_CONFIRMATION_TXT')}</b>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div>
          <Button
            id="a84336e7-cf6f-46db-a3f3-2c2a945c8dc6"
            data-action={'cancel'}
            bsStyle="link"
            className="text-capitalize"
            onClick={doClose}
            type="button"
          >
            {translate('CANCEL')}
          </Button>
          <Button
            id="c198eb7a-54b2-4f5d-acc8-7214fa8fcece"
            className="btn btn-second text-capitalize"
            onClick={() => nextStep(packageData, options, config, 'STEP1')}
          >
            {translate('BACK')}
          </Button>
          <Button
            id="3d78833e-b970-4bbc-8fc9-7888b0085520"
            onClick={save}
            className="btn btn-primary text-capitalize"
            type="submit"
          >
            {translate('ACTION_APPLY')}
          </Button>
        </div>
      </Modal.Footer>
    </>
  )
}

export default ApplyPackageReviewStep
